import { Box, IconButton, Typography } from "@outschool/backpack";
import { faArrowLeft, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { getBasepath } from "@patched/hookrouter";
import React from "react";

import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import { lessonBlockPath, navigateToLessonBlock } from "./utils";

interface ThreadViewLayoutProps {
  postTitle: string;
  postSubtitle?: string;
  /** Lesson item (class post or assignment) the title links to. */
  blockUid?: string;
  showBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

export default function ThreadViewLayout({
  postTitle,
  postSubtitle,
  blockUid,
  showBackButton = true,
  onBack,
  onClose,
  children,
}: ThreadViewLayoutProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadViewLayout",
  );
  const { section, leader, teacherName } = useMessageTeacherDrawerContext();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          p: 12,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {showBackButton && (
          <IconButton
            icon={faArrowLeft}
            aria-label={t("Back to threads")}
            variant="text"
            onClick={onBack}
            size="small"
          />
        )}
        <UserHeadshotImage user={leader} size={40} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {teacherName}
          </Typography>
          {Boolean(section.end_time) && (
            <Typography variant="caption" color="text.secondary">
              {t("Teacher access ends {{date}}", {
                date: dayjs(section.end_time).format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>
        <IconButton
          icon={faTimes}
          aria-label={t("Close messages")}
          variant="text"
          onClick={onClose}
        />
      </Box>

      <Box
        sx={{
          p: 16,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {blockUid ? (
            <a
              href={getBasepath() + lessonBlockPath(section.uid, blockUid)}
              style={{ color: "inherit" }}
              onClick={event => {
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                onClose();
                navigateToLessonBlock(section.uid, blockUid);
              }}
            >
              {postTitle}
            </a>
          ) : (
            postTitle
          )}
        </Typography>
        {postSubtitle && (
          <Typography variant="body2" color="text.secondary">
            {postSubtitle}
          </Typography>
        )}
      </Box>

      {children}
    </Box>
  );
}
